import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { pipelinesApi, type ApiListResponse } from '@/lib/api'
import { STAGE_LOST, stageColorMap } from '@/lib/statusColors'
import { useNotificationStore } from './notifications'

export interface Stage {
  _id: string
  name: string
  order: number
  probability: number
  color: string
  type?: 'open' | 'won' | 'lost'
}

export interface Pipeline {
  _id: string
  name: string
  description?: string
  active: boolean
  default: boolean
  assignedUsers: string[]
  stages: Stage[]
  orgId: string
  createdBy: string
  updatedBy: string
  createdAt: string
  updatedAt: string
}

export const usePipelinesStore = defineStore('pipelines', () => {
  const pipelines = ref<Pipeline[]>([])
  const currentPipeline = ref<Pipeline | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  
  const notifications = useNotificationStore()

  // Computed
  const defaultPipeline = computed(() => 
    pipelines.value.find(p => p.default)
  )

  const activePipelines = computed(() => 
    pipelines.value.filter(p => p.active)
  )

  const currentPipelineStages = computed(() => 
    currentPipeline.value?.stages.sort((a, b) => a.order - b.order) || []
  )

  const stageById = computed(() => {
    const stageMap: Record<string, Stage> = {}
    pipelines.value.forEach(pipeline => {
      pipeline.stages.forEach(stage => {
        stageMap[stage._id] = stage
      })
    })
    return stageMap
  })

  // Stage colour comes from position (the blue ramp, won green, lost grey), ranked within each
  // pipeline; the colour picked in the DB (`stage.color`) is ignored.
  const stageColorById = computed(() => {
    const colors: Record<string, string> = {}
    pipelines.value.forEach(pipeline => {
      Object.assign(colors, stageColorMap(pipeline.stages))
    })
    return colors
  })

  // Actions
  async function fetchPipelines(): Promise<void> {
    loading.value = true
    error.value = null
    
    try {
      const response: ApiListResponse<Pipeline> = await pipelinesApi.list()
      pipelines.value = response.data
      
      // Set current pipeline to default if none selected
      if (!currentPipeline.value && defaultPipeline.value) {
        currentPipeline.value = defaultPipeline.value
      }
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Failed to fetch pipelines'
      notifications.error('Failed to load pipelines')
    } finally {
      loading.value = false
    }
  }

  async function fetchPipeline(id: string): Promise<Pipeline | null> {
    loading.value = true
    error.value = null
    
    try {
      const response = await pipelinesApi.get(id)
      const pipeline = response.data
      
      // Update pipeline in store if it exists
      const index = pipelines.value.findIndex(p => p._id === id)
      if (index !== -1) {
        pipelines.value[index] = pipeline
      } else {
        pipelines.value.push(pipeline)
      }
      
      return pipeline
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Failed to fetch pipeline'
      notifications.error('Failed to load pipeline')
      return null
    } finally {
      loading.value = false
    }
  }

  /**
   * GET /pipelines/:id like `fetchPipeline` (the answer goes into `pipelines`), for a page that must
   * tell "the pipeline is gone" (a 404) from "it could not be loaded" (any other failure): a failure
   * returns its HTTP status (0 when there was no answer) and no toast, so the page says what it means.
   */
  async function fetchPipelineResult(id: string): Promise<{ pipeline: Pipeline | null; status: number | null }> {
    try {
      const response = await pipelinesApi.get(id)
      const pipeline = response.data
      const index = pipelines.value.findIndex(p => p._id === id)
      if (index !== -1) {
        pipelines.value[index] = pipeline
      } else {
        pipelines.value.push(pipeline)
      }
      return { pipeline, status: null }
    } catch (err: any) {
      const status = err?.response?.status
      return { pipeline: null, status: typeof status === 'number' ? status : 0 }
    }
  }

  async function createPipeline(pipelineData: Partial<Pipeline>): Promise<Pipeline | null> {
    loading.value = true
    error.value = null
    
    try {
      const response = await pipelinesApi.create(pipelineData)
      const newPipeline = response.data
      pipelines.value.push(newPipeline)
      notifications.success('Pipeline created successfully')
      return newPipeline
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Failed to create pipeline'
      notifications.error('Failed to create pipeline')
      return null
    } finally {
      loading.value = false
    }
  }

  async function updatePipeline(id: string, pipelineData: Partial<Pipeline>): Promise<Pipeline | null> {
    loading.value = true
    error.value = null
    
    try {
      const response = await pipelinesApi.update(id, pipelineData)
      const updatedPipeline = response.data
      
      const index = pipelines.value.findIndex(p => p._id === id)
      if (index !== -1) {
        pipelines.value[index] = updatedPipeline
      }
      
      // Update current pipeline if it's the one being updated
      if (currentPipeline.value?._id === id) {
        currentPipeline.value = updatedPipeline
      }
      
      notifications.success('Pipeline updated successfully')
      return updatedPipeline
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Failed to update pipeline'
      notifications.error('Failed to update pipeline')
      return null
    } finally {
      loading.value = false
    }
  }

  async function deletePipeline(id: string): Promise<boolean> {
    loading.value = true
    error.value = null
    
    try {
      await pipelinesApi.delete(id)
      pipelines.value = pipelines.value.filter(p => p._id !== id)
      
      // Reset current pipeline if it was deleted
      if (currentPipeline.value?._id === id) {
        currentPipeline.value = defaultPipeline.value || null
      }
      
      notifications.success('Pipeline deleted successfully')
      return true
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Failed to delete pipeline'
      notifications.error('Failed to delete pipeline')
      return false
    } finally {
      loading.value = false
    }
  }

  async function reorderStages(pipelineId: string, stages: Stage[]): Promise<boolean> {
    loading.value = true
    error.value = null
    
    try {
      const response = await pipelinesApi.reorderStages(pipelineId, stages)
      const updatedPipeline = response.data
      
      const index = pipelines.value.findIndex(p => p._id === pipelineId)
      if (index !== -1) {
        pipelines.value[index] = updatedPipeline
      }
      
      // Update current pipeline if it's the one being updated
      if (currentPipeline.value?._id === pipelineId) {
        currentPipeline.value = updatedPipeline
      }
      
      notifications.success('Stage order updated successfully')
      return true
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Failed to reorder stages'
      notifications.error('Failed to update stage order')
      return false
    } finally {
      loading.value = false
    }
  }

  /**
   * Deals per stage of a pipeline, keyed by stage id (0 for an empty stage; archived deals
   * count, deleted ones do not). Errors propagate to the caller.
   */
  async function fetchStageDealCounts(pipelineId: string): Promise<Record<string, number>> {
    const response = await pipelinesApi.stageDealCounts(pipelineId)
    const counts: Record<string, number> = {}
    for (const row of response.data ?? []) {
      counts[row.stageId] = row.deals
    }
    return counts
  }

  /**
   * Delete a stage, moving its deals to `moveTo` first (required when it has any — the API
   * answers 409 otherwise). Replaces the pipeline in state and returns how many deals moved.
   * Errors propagate so the caller can show the API's message.
   */
  async function deleteStage(pipelineId: string, stageId: string, moveTo?: string): Promise<number> {
    const response = await pipelinesApi.deleteStage<Pipeline>(pipelineId, stageId, moveTo)
    upsertPipeline(response.data)
    return response.movedDeals
  }

  function setCurrentPipeline(pipeline: Pipeline | null): void {
    currentPipeline.value = pipeline
  }

  function setCurrentPipelineById(id: string): void {
    const pipeline = pipelines.value.find(p => p._id === id)
    if (pipeline) {
      currentPipeline.value = pipeline
    }
  }

  function getStageName(stageId: string): string {
    const stage = stageById.value[stageId]
    return stage?.name || 'Unknown Stage'
  }

  function getStageColor(stageId: string): string {
    return stageColorById.value[stageId] ?? STAGE_LOST
  }

  function clearPipelines(): void {
    pipelines.value = []
    currentPipeline.value = null
    error.value = null
  }

  /**
   * Put a pipeline saved elsewhere (the New / Edit pipeline dialog calls the API itself) into the
   * store exactly as createPipeline / updatePipeline do: replace it in the list (and as the current
   * pipeline when it is that one), or add it to the list when it is new.
   */
  function upsertPipeline(saved: Pipeline): void {
    const index = pipelines.value.findIndex(p => p._id === saved._id)
    if (index === -1) {
      pipelines.value.push(saved)
    } else {
      pipelines.value[index] = saved
    }
    if (currentPipeline.value?._id === saved._id) {
      currentPipeline.value = saved
    }
  }

  return {
    // State
    pipelines,
    currentPipeline,
    loading,
    error,
    
    // Computed
    defaultPipeline,
    activePipelines,
    currentPipelineStages,
    stageById,
    
    // Actions
    fetchPipelines,
    fetchPipeline,
    fetchPipelineResult,
    createPipeline,
    updatePipeline,
    deletePipeline,
    reorderStages,
    fetchStageDealCounts,
    deleteStage,
    setCurrentPipeline,
    setCurrentPipelineById,
    getStageName,
    getStageColor,
    clearPipelines,
    upsertPipeline,
  }
})